import { aesGcmOpen } from '@/crypto/aes-gcm'
import { deriveMasterKeyInWorker } from '@/crypto/argon2-worker-client'
import { base64ToBytes, hexToBytes } from '@/crypto/encoding'
import { deriveStretchedMasterKeyEnc } from '@/crypto/hkdf'
import { sessionStore } from './sessionStore'

/**
 * 現マスターパスワードの照合 (完全クライアント側、ゼロ知識)。
 *
 * sessionStore.material の KDF パラメータでマスターパスワードから鍵を導出し、
 * `protectedSymmetricKey` を AES-GCM open して、得られた SK が現セッションの SK と
 * 一致するかを定数時間比較で返す。マスターパスワードはサーバに送らない。
 *
 * メール変更 / エクスポート等、機微操作の前段でユーザー確認に使う。
 */

/** 1 つでも byte が違えば false。長さ違いも false (定数時間比較)。 */
const bytesEqual = (a: Uint8Array, b: Uint8Array): boolean => {
  if (a.length !== b.length) return false
  let diff = 0
  for (let i = 0; i < a.length; i++) {
    diff |= a[i]! ^ b[i]!
  }

  return diff === 0
}

export const verifyMasterPasswordMatchesSession = async (password: string): Promise<boolean> => {
  const state = sessionStore.getState()
  if (state.kind !== 'unlocked') return false
  const material = state.material

  const kdfSaltBytes = hexToBytes(material.kdfSalt)

  const mk = await deriveMasterKeyInWorker(password, kdfSaltBytes, {
    memoryCost: material.kdfParams.memoryCost,
    timeCost: material.kdfParams.timeCost,
    parallelism: material.kdfParams.parallelism,
  })

  try {
    const smkEnc = await deriveStretchedMasterKeyEnc(mk)
    const iv = base64ToBytes(material.protectedSymmetricKeyIv)
    const sealed = base64ToBytes(material.protectedSymmetricKey)
    const candidateSk = await aesGcmOpen(smkEnc, iv, sealed)

    return bytesEqual(candidateSk, state.symmetricKey)
  } catch {
    // AES-GCM tag mismatch = wrong password
    return false
  } finally {
    // mk は worker 経由で Uint8Array が返るため zeroize (best-effort)
    mk.fill(0)
  }
}
